/*
 * consent/consent.ts — o registro da escolha de cookies (LGPD, opt-in).
 *
 * A escolha mora num cookie de 1ª parte (`seesforce_consent`), e não no
 * localStorage, porque é o artefato que a ANPD/CNIL esperam ver como prova:
 * versão da política aceita, um id aleatório (sem ligação com a conta), a data e
 * a decisão por categoria. Sem registro válido — ausente, vencido, ilegível ou de
 * uma versão anterior da política — toda categoria opcional está NEGADA e o
 * aviso volta a perguntar.
 *
 * O estado é um store de módulo com assinantes: o React lê por
 * `useSyncExternalStore` (useConsent.ts) e os módulos sem React (o loader do
 * AdSense, o Consent Mode do Google) assinam direto.
 */

/** Categorias com interruptor. "Necessários" não entra: nunca é desligável. */
export type OptionalCategory = 'advertising';
export type ConsentCategory = 'necessary' | OptionalCategory;

export type ConsentChoices = Record<OptionalCategory, boolean>;

/**
 * Versão da política de cookies a que a escolha se refere. Mudou o que a seção
 * 11 da política declara (uma categoria, um fornecedor)? Suba a versão: todo
 * registro anterior deixa de valer e a pergunta volta.
 */
export const CONSENT_POLICY_VERSION = '2026-10-18';

export const CONSENT_COOKIE = 'seesforce_consent';

/**
 * Dispensa do aviso informativo (quando só há necessários). Preferência da
 * pessoa, no localStorage — não é registro de consentimento.
 */
export const COOKIE_NOTICE_KEY = 'habilita.cookie-notice';

/** Validade do registro: 12 meses, o teto usual das autoridades europeias e da ANPD. */
export const CONSENT_MAX_AGE_SECONDS = 365 * 24 * 60 * 60;

export interface ConsentRecord {
  /** Versão da política aceita. */
  v: string;
  /** Id aleatório do registro — prova da escolha, sem identificar a pessoa. */
  id: string;
  /** Quando a escolha foi feita (ISO 8601). */
  ts: string;
  /** Decisão por categoria opcional. */
  c: ConsentChoices;
}

export interface ConsentState {
  /** Há uma escolha válida registrada (o aviso não precisa perguntar). */
  decided: boolean;
  /** O navegador envia o sinal Global Privacy Control. */
  gpc: boolean;
  /** Decisão vigente por categoria opcional (negada sem escolha). */
  choices: ConsentChoices;
  record: ConsentRecord | null;
}

const DENIED: ConsentChoices = { advertising: false };
const GRANTED: ConsentChoices = { advertising: true };

function readGpc(): boolean {
  if (typeof navigator === 'undefined') return false;
  return (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true;
}

function randomId(): string {
  const cryptoApi = globalThis.crypto;
  if (cryptoApi?.randomUUID) return cryptoApi.randomUUID();
  const bytes = new Uint8Array(16);
  cryptoApi?.getRandomValues?.(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

/** Serializa o registro no valor do cookie (JSON compacto, url-encoded). */
export function serializeConsent(record: ConsentRecord): string {
  return encodeURIComponent(JSON.stringify(record));
}

/**
 * Lê o valor do cookie de volta. Devolve null para tudo que não for um registro
 * válido da versão vigente dentro da validade — inclusive JSON malformado.
 */
export function parseConsent(raw: string | null | undefined, now: Date = new Date()): ConsentRecord | null {
  if (!raw) return null;
  let data: unknown;
  try {
    data = JSON.parse(decodeURIComponent(raw));
  } catch {
    return null;
  }
  if (typeof data !== 'object' || data === null) return null;
  const { v, id, ts, c } = data as Partial<ConsentRecord>;
  if (v !== CONSENT_POLICY_VERSION || typeof id !== 'string' || typeof ts !== 'string') return null;
  if (typeof c !== 'object' || c === null || typeof c.advertising !== 'boolean') return null;
  const at = Date.parse(ts);
  if (Number.isNaN(at)) return null;
  // O Max-Age já vence o cookie no navegador; a data dentro dele é a segunda
  // trava, para um cookie regravado à mão ou um relógio que andou para trás.
  if (now.getTime() - at > CONSENT_MAX_AGE_SECONDS * 1000) return null;
  return { v, id, ts, c: { advertising: c.advertising } };
}

function readCookie(): string | null {
  if (typeof document === 'undefined') return null;
  const prefix = `${CONSENT_COOKIE}=`;
  for (const part of document.cookie.split(';')) {
    const entry = part.trim();
    if (entry.startsWith(prefix)) return entry.slice(prefix.length);
  }
  return null;
}

function cookieAttributes(maxAge: number): string {
  const secure = typeof location !== 'undefined' && location.protocol === 'https:' ? '; Secure' : '';
  return `; Path=/; Max-Age=${maxAge}; SameSite=Lax${secure}`;
}

function writeCookie(record: ConsentRecord): void {
  if (typeof document === 'undefined') return;
  document.cookie = `${CONSENT_COOKIE}=${serializeConsent(record)}${cookieAttributes(CONSENT_MAX_AGE_SECONDS)}`;
}

function clearCookie(): void {
  if (typeof document === 'undefined') return;
  document.cookie = `${CONSENT_COOKIE}=${cookieAttributes(0)}`;
}

function stateFrom(record: ConsentRecord | null): ConsentState {
  return {
    decided: record !== null,
    gpc: readGpc(),
    choices: record ? record.c : DENIED,
    record,
  };
}

type Listener = (state: ConsentState) => void;

const listeners = new Set<Listener>();
let current: ConsentState | null = null;

/** O estado vigente, com identidade estável entre mudanças (snapshot do React). */
export function getConsent(): ConsentState {
  if (!current) current = stateFrom(parseConsent(readCookie()));
  return current;
}

/** Necessários sempre; uma categoria opcional só com escolha registrada que a conceda. */
export function hasConsent(category: ConsentCategory): boolean {
  if (category === 'necessary') return true;
  return getConsent().choices[category];
}

function publish(next: ConsentState): void {
  current = next;
  for (const listener of [...listeners]) listener(next);
}

/** Registra a escolha da pessoa (renova id, data e validade). */
export function setConsent(choices: ConsentChoices): ConsentState {
  const record: ConsentRecord = {
    v: CONSENT_POLICY_VERSION,
    id: randomId(),
    ts: new Date().toISOString(),
    c: { advertising: choices.advertising },
  };
  writeCookie(record);
  const next = stateFrom(record);
  publish(next);
  return next;
}

export function acceptAll(): ConsentState {
  return setConsent(GRANTED);
}

export function rejectAll(): ConsentState {
  return setConsent(DENIED);
}

/** Apaga o registro: a próxima visita volta a perguntar. */
export function resetConsent(): ConsentState {
  clearCookie();
  const next = stateFrom(null);
  publish(next);
  return next;
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Evento que reabre o painel de preferências de qualquer ponto da interface. */
export const COOKIE_SETTINGS_EVENT = 'seesforce:cookie-settings';

export function openCookieSettings(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(COOKIE_SETTINGS_EVENT));
}

/** Só para testes: relê o cookie e o sinal GPC na próxima leitura. */
export function __resetConsentCache(): void {
  current = null;
}
